export type VideoSource = 'youtube' | 'peertube' | 'tiktok'
export type VideoOrientation = 'horizontal' | 'vertical'

export interface VideoItem {
  id: string
  url: string
  title: string
  thumbnail?: string
  source: VideoSource
  orientation: VideoOrientation
}

export interface ParsedVideo {
  source: VideoSource
  videoId: string
  embedUrl: string
  orientation: VideoOrientation
}

/**
 * YouTube oEmbed API response
 */
export interface YouTubeOEmbedResponse {
  title: string
  thumbnail_url: string
  author_name: string
  author_url: string
  type: string
  html: string
  provider_name: string
  width: number
  height: number
}

/**
 * Parsed video ID from route parameter
 */
export interface ParsedVideoId {
  source: VideoSource
  videoId: string
  orientation: VideoOrientation
}

/**
 * Parse a prefixed video ID from route parameter
 * Supports: yt-{videoId} for regular videos, short-{videoId} for YouTube Shorts
 */
export function parseVideoIdFromRoute(
  routeId: string,
): ParsedVideoId | undefined {
  if (routeId.startsWith('yt-')) {
    return {
      source: 'youtube',
      videoId: routeId.slice(3),
      orientation: 'horizontal',
    }
  }
  if (routeId.startsWith('short-')) {
    return {
      source: 'youtube',
      videoId: routeId.slice(6),
      orientation: 'vertical',
    }
  }
  return undefined
}

/**
 * Get YouTube thumbnail URL for a video ID
 */
export function getYouTubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
}

/**
 * Get YouTube embed URL for a video ID
 */
export function getYouTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}`
}

/**
 * Parse a video URL and extract platform-specific information
 */
export function parseVideoUrl(url: string): ParsedVideo | undefined {
  try {
    const urlObj = new URL(url)
    const hostname = urlObj.hostname.toLowerCase()

    // YouTube
    if (
      hostname === 'youtube.com' ||
      hostname === 'www.youtube.com' ||
      hostname === 'm.youtube.com'
    ) {
      // YouTube Shorts
      if (urlObj.pathname.startsWith('/shorts/')) {
        const videoId = urlObj.pathname.split('/shorts/')[1]?.split('/')[0]
        if (videoId) {
          return {
            source: 'youtube',
            videoId,
            embedUrl: `https://www.youtube.com/embed/${videoId}`,
            orientation: 'vertical',
          }
        }
      }
      // Regular YouTube video
      const videoId = urlObj.searchParams.get('v')
      if (videoId) {
        return {
          source: 'youtube',
          videoId,
          embedUrl: `https://www.youtube.com/embed/${videoId}`,
          orientation: 'horizontal',
        }
      }
    }

    // YouTube short URLs (youtu.be)
    if (hostname === 'youtu.be') {
      const videoId = urlObj.pathname.slice(1).split('/')[0]
      if (videoId) {
        return {
          source: 'youtube',
          videoId,
          embedUrl: `https://www.youtube.com/embed/${videoId}`,
          orientation: 'horizontal',
        }
      }
    }

    // TikTok
    if (hostname === 'tiktok.com' || hostname === 'www.tiktok.com') {
      // Pattern: /@username/video/VIDEO_ID
      const match = urlObj.pathname.match(/\/@[^/]+\/video\/(\d+)/)
      if (match) {
        const videoId = match[1]
        return {
          source: 'tiktok',
          videoId,
          embedUrl: `https://www.tiktok.com/embed/v2/${videoId}`,
          orientation: 'vertical',
        }
      }
    }

    // TikTok short URLs (vm.tiktok.com)
    if (hostname === 'vm.tiktok.com') {
      // These are short links that redirect, use the path as ID
      const videoId = urlObj.pathname.slice(1)
      if (videoId) {
        return {
          source: 'tiktok',
          videoId,
          embedUrl: `https://www.tiktok.com/embed/v2/${videoId}`,
          orientation: 'vertical',
        }
      }
    }

    // PeerTube - federated instances with various domains
    // Pattern: /videos/watch/VIDEO_ID or /w/VIDEO_ID
    if (
      urlObj.pathname.startsWith('/videos/watch/') ||
      urlObj.pathname.startsWith('/w/')
    ) {
      let videoId: string | undefined
      if (urlObj.pathname.startsWith('/videos/watch/')) {
        videoId = urlObj.pathname.split('/videos/watch/')[1]?.split('/')[0]
      } else if (urlObj.pathname.startsWith('/w/')) {
        videoId = urlObj.pathname.split('/w/')[1]?.split('/')[0]
      }

      if (videoId) {
        return {
          source: 'peertube',
          videoId,
          embedUrl: `https://${urlObj.hostname}/videos/embed/${videoId}`,
          orientation: 'horizontal',
        }
      }
    }

    return undefined
  } catch {
    return undefined
  }
}

/**
 * Get a video item by its ID from the hardcoded list
 */
export function getVideoById(id: string): VideoItem | undefined {
  return VIDEOS_DATA.find(video => video.id === id)
}

/**
 * Hardcoded videos data - mock data for pagination testing
 * In production, this will be replaced with API calls
 */
export const VIDEOS_DATA: VideoItem[] = [
  // YouTube Horizontal Videos
  {
    id: 'yt-dQw4w9WgXcQ',
    url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    title: 'Rick Astley - Never Gonna Give You Up',
    thumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-jNQXAC9IVRw',
    url: 'https://www.youtube.com/watch?v=jNQXAC9IVRw',
    title: 'Me at the zoo',
    thumbnail: 'https://img.youtube.com/vi/jNQXAC9IVRw/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-9bZkp7q19f0',
    url: 'https://www.youtube.com/watch?v=9bZkp7q19f0',
    title: 'PSY - Gangnam Style',
    thumbnail: 'https://img.youtube.com/vi/9bZkp7q19f0/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-kJQP7kiw5Fk',
    url: 'https://www.youtube.com/watch?v=kJQP7kiw5Fk',
    title: 'Luis Fonsi - Despacito ft. Daddy Yankee',
    thumbnail: 'https://img.youtube.com/vi/kJQP7kiw5Fk/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-JGwWNGJdvx8',
    url: 'https://www.youtube.com/watch?v=JGwWNGJdvx8',
    title: 'Ed Sheeran - Shape of You',
    thumbnail: 'https://img.youtube.com/vi/JGwWNGJdvx8/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-RgKAFK5djSk',
    url: 'https://www.youtube.com/watch?v=RgKAFK5djSk',
    title: 'Wiz Khalifa - See You Again ft. Charlie Puth',
    thumbnail: 'https://img.youtube.com/vi/RgKAFK5djSk/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-OPf0YbXqDm0',
    url: 'https://www.youtube.com/watch?v=OPf0YbXqDm0',
    title: 'Mark Ronson - Uptown Funk ft. Bruno Mars',
    thumbnail: 'https://img.youtube.com/vi/OPf0YbXqDm0/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-pRpeEdMmmQ0',
    url: 'https://www.youtube.com/watch?v=pRpeEdMmmQ0',
    title: 'Shakira - Waka Waka (This Time for Africa)',
    thumbnail: 'https://img.youtube.com/vi/pRpeEdMmmQ0/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-fJ9rUzIMcZQ',
    url: 'https://www.youtube.com/watch?v=fJ9rUzIMcZQ',
    title: 'Queen - Bohemian Rhapsody',
    thumbnail: 'https://img.youtube.com/vi/fJ9rUzIMcZQ/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-hTWKbfoikeg',
    url: 'https://www.youtube.com/watch?v=hTWKbfoikeg',
    title: 'Nirvana - Smells Like Teen Spirit',
    thumbnail: 'https://img.youtube.com/vi/hTWKbfoikeg/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-YQHsXMglC9A',
    url: 'https://www.youtube.com/watch?v=YQHsXMglC9A',
    title: 'Adele - Hello',
    thumbnail: 'https://img.youtube.com/vi/YQHsXMglC9A/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-CevxZvSJLk8',
    url: 'https://www.youtube.com/watch?v=CevxZvSJLk8',
    title: 'Katy Perry - Roar',
    thumbnail: 'https://img.youtube.com/vi/CevxZvSJLk8/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-e-ORhEE9VVg',
    url: 'https://www.youtube.com/watch?v=e-ORhEE9VVg',
    title: 'Taylor Swift - Blank Space',
    thumbnail: 'https://img.youtube.com/vi/e-ORhEE9VVg/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-lp-EO5I60KA',
    url: 'https://www.youtube.com/watch?v=lp-EO5I60KA',
    title: 'Eminem - Lose Yourself',
    thumbnail: 'https://img.youtube.com/vi/lp-EO5I60KA/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-hT_nvWreIhg',
    url: 'https://www.youtube.com/watch?v=hT_nvWreIhg',
    title: "Guns N' Roses - November Rain",
    thumbnail: 'https://img.youtube.com/vi/hT_nvWreIhg/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  // YouTube Shorts (Vertical)
  {
    id: 'short-abc123def',
    url: 'https://www.youtube.com/shorts/abc123def',
    title: 'Quick cooking tip - Perfect eggs every time',
    thumbnail: 'https://img.youtube.com/vi/abc123def/hqdefault.jpg',
    source: 'youtube',
    orientation: 'vertical',
  },
  {
    id: 'short-xyz789ghi',
    url: 'https://www.youtube.com/shorts/xyz789ghi',
    title: 'Cute cat doing tricks',
    thumbnail: 'https://img.youtube.com/vi/xyz789ghi/hqdefault.jpg',
    source: 'youtube',
    orientation: 'vertical',
  },
  {
    id: 'short-mno456pqr',
    url: 'https://www.youtube.com/shorts/mno456pqr',
    title: 'Life hack: How to fold a shirt in 2 seconds',
    thumbnail: 'https://img.youtube.com/vi/mno456pqr/hqdefault.jpg',
    source: 'youtube',
    orientation: 'vertical',
  },
  {
    id: 'short-stu123vwx',
    url: 'https://www.youtube.com/shorts/stu123vwx',
    title: 'Amazing magic trick revealed',
    thumbnail: 'https://img.youtube.com/vi/stu123vwx/hqdefault.jpg',
    source: 'youtube',
    orientation: 'vertical',
  },
  {
    id: 'short-yza789bcd',
    url: 'https://www.youtube.com/shorts/yza789bcd',
    title: 'Dance tutorial: Basic moves for beginners',
    thumbnail: 'https://img.youtube.com/vi/yza789bcd/hqdefault.jpg',
    source: 'youtube',
    orientation: 'vertical',
  },
  {
    id: 'short-efg012hij',
    url: 'https://www.youtube.com/shorts/efg012hij',
    title: 'Satisfying slime ASMR compilation',
    thumbnail: 'https://img.youtube.com/vi/efg012hij/hqdefault.jpg',
    source: 'youtube',
    orientation: 'vertical',
  },
  {
    id: 'short-klm345nop',
    url: 'https://www.youtube.com/shorts/klm345nop',
    title: 'Travel vlog: Hidden gem in Paris',
    thumbnail: 'https://img.youtube.com/vi/klm345nop/hqdefault.jpg',
    source: 'youtube',
    orientation: 'vertical',
  },
  {
    id: 'short-qrs678tuv',
    url: 'https://www.youtube.com/shorts/qrs678tuv',
    title: 'Fitness: 30-second ab workout',
    thumbnail: 'https://img.youtube.com/vi/qrs678tuv/hqdefault.jpg',
    source: 'youtube',
    orientation: 'vertical',
  },
  {
    id: 'short-wxy901zab',
    url: 'https://www.youtube.com/shorts/wxy901zab',
    title: 'Comedy skit: When your alarm goes off',
    thumbnail: 'https://img.youtube.com/vi/wxy901zab/hqdefault.jpg',
    source: 'youtube',
    orientation: 'vertical',
  },
  {
    id: 'short-cde234fgh',
    url: 'https://www.youtube.com/shorts/cde234fgh',
    title: 'DIY craft: Making jewelry at home',
    thumbnail: 'https://img.youtube.com/vi/cde234fgh/hqdefault.jpg',
    source: 'youtube',
    orientation: 'vertical',
  },
  // TikTok Videos (Vertical)
  {
    id: 'tt-7123456789012345678',
    url: 'https://www.tiktok.com/@creator1/video/7123456789012345678',
    title: 'Viral dance challenge - Summer vibes',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7234567890123456789',
    url: 'https://www.tiktok.com/@creator2/video/7234567890123456789',
    title: 'Recipe: 5-minute healthy breakfast',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7345678901234567890',
    url: 'https://www.tiktok.com/@creator3/video/7345678901234567890',
    title: 'Storytime: The craziest thing that happened to me',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7456789012345678901',
    url: 'https://www.tiktok.com/@creator4/video/7456789012345678901',
    title: 'Pet compilation: Dogs being derpy',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7567890123456789012',
    url: 'https://www.tiktok.com/@creator5/video/7567890123456789012',
    title: 'Makeup tutorial: Natural everyday look',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7678901234567890123',
    url: 'https://www.tiktok.com/@creator6/video/7678901234567890123',
    title: 'Tech review: Latest smartphone unboxing',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7789012345678901234',
    url: 'https://www.tiktok.com/@creator7/video/7789012345678901234',
    title: 'Fashion haul: Thrift store finds',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7890123456789012345',
    url: 'https://www.tiktok.com/@creator8/video/7890123456789012345',
    title: 'Gaming highlights: Epic plays compilation',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7901234567890123456',
    url: 'https://www.tiktok.com/@creator9/video/7901234567890123456',
    title: 'Art process: Digital painting timelapse',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7012345678901234567',
    url: 'https://www.tiktok.com/@creator10/video/7012345678901234567',
    title: 'POV: First day at new job',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7112233445566778899',
    url: 'https://www.tiktok.com/@creator11/video/7112233445566778899',
    title: 'Music cover: Acoustic guitar session',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7223344556677889900',
    url: 'https://www.tiktok.com/@creator12/video/7223344556677889900',
    title: 'Workout routine: No equipment needed',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7334455667788990011',
    url: 'https://www.tiktok.com/@creator13/video/7334455667788990011',
    title: 'Study with me: Cozy library ambience',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7445566778899001122',
    url: 'https://www.tiktok.com/@creator14/video/7445566778899001122',
    title: 'Prank video: Surprising my roommate',
    source: 'tiktok',
    orientation: 'vertical',
  },
  {
    id: 'tt-7556677889900112233',
    url: 'https://www.tiktok.com/@creator15/video/7556677889900112233',
    title: 'GRWM: Date night edition',
    source: 'tiktok',
    orientation: 'vertical',
  },
  // PeerTube Videos (Horizontal - federated/open source)
  {
    id: 'pt-framatube-abc123',
    url: 'https://framatube.org/videos/watch/abc123',
    title: 'Introduction to Open Source Software',
    source: 'peertube',
    orientation: 'horizontal',
  },
  {
    id: 'pt-framatube-def456',
    url: 'https://framatube.org/videos/watch/def456',
    title: 'Linux Tutorial for Beginners',
    source: 'peertube',
    orientation: 'horizontal',
  },
  {
    id: 'pt-framatube-ghi789',
    url: 'https://framatube.org/videos/watch/ghi789',
    title: 'Privacy Tools: Protecting Your Data',
    source: 'peertube',
    orientation: 'horizontal',
  },
  {
    id: 'pt-tilvids-jkl012',
    url: 'https://tilvids.com/videos/watch/jkl012',
    title: 'Documentary: The History of the Internet',
    source: 'peertube',
    orientation: 'horizontal',
  },
  {
    id: 'pt-tilvids-mno345',
    url: 'https://tilvids.com/videos/watch/mno345',
    title: 'Science Explained: Quantum Computing',
    source: 'peertube',
    orientation: 'horizontal',
  },
  {
    id: 'pt-tilvids-pqr678',
    url: 'https://tilvids.com/videos/watch/pqr678',
    title: 'Coding Tutorial: Build a REST API',
    source: 'peertube',
    orientation: 'horizontal',
  },
  {
    id: 'pt-peertube-stu901',
    url: 'https://peertube.social/videos/watch/stu901',
    title: 'Web Development: Modern CSS Techniques',
    source: 'peertube',
    orientation: 'horizontal',
  },
  {
    id: 'pt-peertube-vwx234',
    url: 'https://peertube.social/videos/watch/vwx234',
    title: 'Conference Talk: Future of Decentralized Web',
    source: 'peertube',
    orientation: 'horizontal',
  },
  {
    id: 'pt-peertube-yza567',
    url: 'https://peertube.social/videos/watch/yza567',
    title: 'Tutorial: Self-hosting Your Services',
    source: 'peertube',
    orientation: 'horizontal',
  },
  {
    id: 'pt-peertube-bcd890',
    url: 'https://peertube.social/videos/watch/bcd890',
    title: 'Open Hardware: Building Your Own Computer',
    source: 'peertube',
    orientation: 'horizontal',
  },
  // More YouTube Horizontal for variety
  {
    id: 'yt-L_jWHffIx5E',
    url: 'https://www.youtube.com/watch?v=L_jWHffIx5E',
    title: 'Smash Mouth - All Star',
    thumbnail: 'https://img.youtube.com/vi/L_jWHffIx5E/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-dQw4w9WgXcR',
    url: 'https://www.youtube.com/watch?v=dQw4w9WgXcR',
    title: 'Travel Vlog: Exploring Tokyo at Night',
    thumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcR/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-abc123xyz45',
    url: 'https://www.youtube.com/watch?v=abc123xyz45',
    title: 'Cooking Masterclass: Italian Pasta from Scratch',
    thumbnail: 'https://img.youtube.com/vi/abc123xyz45/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-def456uvw78',
    url: 'https://www.youtube.com/watch?v=def456uvw78',
    title: 'Documentary: Wildlife in the Amazon',
    thumbnail: 'https://img.youtube.com/vi/def456uvw78/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-ghi789rst01',
    url: 'https://www.youtube.com/watch?v=ghi789rst01',
    title: 'Photography Tips: Perfect Landscape Shots',
    thumbnail: 'https://img.youtube.com/vi/ghi789rst01/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-jkl012mno34',
    url: 'https://www.youtube.com/watch?v=jkl012mno34',
    title: 'Guitar Lesson: Learn Your First Song',
    thumbnail: 'https://img.youtube.com/vi/jkl012mno34/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-pqr567stu89',
    url: 'https://www.youtube.com/watch?v=pqr567stu89',
    title: 'Home Workout: Full Body HIIT Training',
    thumbnail: 'https://img.youtube.com/vi/pqr567stu89/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-vwx234yza56',
    url: 'https://www.youtube.com/watch?v=vwx234yza56',
    title: 'Science Experiment: What Happens When...',
    thumbnail: 'https://img.youtube.com/vi/vwx234yza56/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-bcd789efg01',
    url: 'https://www.youtube.com/watch?v=bcd789efg01',
    title: 'Movie Review: Top Films of the Year',
    thumbnail: 'https://img.youtube.com/vi/bcd789efg01/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
  {
    id: 'yt-hij234klm56',
    url: 'https://www.youtube.com/watch?v=hij234klm56',
    title: 'Car Review: Electric Vehicle Comparison',
    thumbnail: 'https://img.youtube.com/vi/hij234klm56/hqdefault.jpg',
    source: 'youtube',
    orientation: 'horizontal',
  },
]
